import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RESET_TODO_PERFORMANCE } from "../redux/constants";
import { useEffect, useState } from "react";
import Loader from "react-spinner-loader";
import useDebounce from "../Hook/useDebounce";

// Importing Datatable
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnSizing,
} from "@tanstack/react-table";

// needed for table body level scope DnD setup
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

// needed for row & cell level scope DnD setup
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Super_Admin } from "../constant/AuthenticationConstant";

const Table = ({
  children,
  heads,
  rows,
  tClass,
  hClass,
  bClass,
  thClass,
  tdClass,
  setSort,
  sort,
}) => {
  const handleHeaderClick = (item) => {
    if (item.isSort) {
      if (sort?.label === item.headName && sort?.type === "asc") {
        setSort({
          label: item.headName,
          headName: item.value,
          type: "desc",
        });
      } else {
        setSort({
          label: item.headName,
          headName: item.value,
          type: "asc",
        });
      }
    }
  };
  return (
    <table className={tClass}>
      <thead className={`${hClass}`}>
        <tr>
          {(heads || (Array.isArray(rows) ? Object.keys(rows[0]) : [])).map(
            (item, i) => (
              <th key={i} className={`${thClass} ${item?.class ? item.class : ""}`}>
                <span
                  className={`${
                    item.isSort ? "cursor-pointer" : "cursor-default"
                  }`}
                  onClick={() => handleHeaderClick(item)}
                >
                  {item.headName}
                </span>
                {item.isSort && (
                  <svg
                    width="12"
                    height="16"
                    viewBox="0 0 8 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="inline ml-2 -mt-[2px]"
                  >
                    <path
                      d="M6.82673 4.88075C6.69853 4.8809 6.57156 4.85571 6.45312 4.80662C6.33468 4.75754 6.22711 4.68553 6.13659 4.59474L3.89826 2.35647L1.65994 4.59474C1.47634 4.77505 1.22896 4.87555 0.971628 4.87439C0.714298 4.87322 0.46784 4.77048 0.285878 4.58852C0.103916 4.40655 0.00117537 4.1601 1.00221e-05 3.90277C-0.00115533 3.64544 0.0993491 3.39806 0.279655 3.21446L3.20812 0.285989C3.29874 0.195323 3.40633 0.1234 3.52476 0.0743293C3.64318 0.0252584 3.77011 0 3.89829 0C4.02648 0 4.15341 0.0252584 4.27183 0.0743293C4.39026 0.1234 4.49785 0.195323 4.58847 0.285989L7.51693 3.21446C7.65341 3.35098 7.74635 3.5249 7.784 3.71423C7.82164 3.90356 7.80231 4.09981 7.72843 4.27816C7.65456 4.4565 7.52947 4.60894 7.36897 4.7162C7.20847 4.82346 7.01977 4.88072 6.82673 4.88075Z"
                      className={`cursor-pointer fill-current ${
                        sort?.label === item.headName && sort?.type === "asc"
                          ? "text-[--bg-color-500]"
                          : "text-[#9C9C9C]"
                      }`}
                      onClick={() =>
                        setSort({
                          label: item.headName,
                          headName: item.value,
                          type: "asc",
                        })
                      }
                    />
                    <path
                      d="M3.56018 11.9259C3.67862 11.975 3.80559 12.0001 3.93379 12C4.062 12.0001 4.18897 11.975 4.30741 11.9259C4.42585 11.8768 4.53342 11.8048 4.62394 11.714L7.5524 8.78552C7.73355 8.60208 7.83477 8.35444 7.83396 8.09664C7.83316 7.83884 7.73039 7.59183 7.5481 7.40953C7.36581 7.22724 7.11881 7.12446 6.86101 7.12364C6.60321 7.12283 6.35556 7.22404 6.17212 7.40517L3.93379 9.6435L1.69547 7.40523C1.51187 7.22493 1.26449 7.12442 1.00716 7.12559C0.749827 7.12675 0.503369 7.22949 0.321407 7.41145C0.139445 7.59342 0.0367045 7.83987 0.0355392 8.09721C0.0343738 8.35454 0.134878 8.60191 0.315185 8.78552L3.24365 11.714C3.33417 11.8048 3.44174 11.8768 3.56018 11.9259Z"
                      className={`cursor-pointer fill-current ${
                        sort?.label === item.headName && sort?.type === "desc"
                          ? "text-[--bg-color-500]"
                          : "text-[#9C9C9C]"
                      }`}
                      onClick={() =>
                        setSort({
                          label: item.headName,
                          headName: item.value,
                          type: "desc",
                        })
                      }
                    />
                  </svg>
                )}
              </th>
            )
          )}
        </tr>
      </thead>

      <tbody className={bClass}>
        {children ||
          (Array.isArray(rows) &&
            rows.map((row) => <TableRow tdClass={tdClass} row={row} />))}
      </tbody>
    </table>
  );
};

export const TableRow = ({ children, row, trClass, tdClass, prefix, id }) => {
  const navigate = useNavigate();
  const { company_id, email } = useParams();
  const isEmbedded = Boolean(company_id && email);
  const dispatch = useDispatch();

  return (
    <tr
      className={`${trClass || ""}`}
      onClick={() => {
        if (Object.keys(row)[0] === "job_id") {
          dispatch({ type: RESET_TODO_PERFORMANCE });
          navigate(`/todo/performance/${id.activity_assignment_id}`);
        } else if (Object.keys(row)[1] === "job") {
          dispatch({ type: RESET_TODO_PERFORMANCE });
          !isEmbedded
            ? navigate(
                `/todo/performance/${id.activity_assignment_id}?activity_id=${id.activity_id}`
              )
            : navigate(
                `/activity/performance/embedded/${company_id}/${id.activity_assignment_id}/${email}?activity_id=${id.activity_id}`
              );
        }
      }}
    >
      {Object.keys(row).map((key, i) => (
        <td title={row[key]} key={i} className={tdClass}>
          {i === 0 && prefix ? (
            <>
              {prefix} <div className="font-medium ml-2">{row[key]}</div>
            </>
          ) : (
            row[key]
          )}
        </td>
      ))}
      {children}
    </tr>
  );
};

/*-------------------- Column Draggable Table --------------------*/
// Draggable Table Header Component
const DraggableTableHeader = ({
  table,
  header,
  isTableChange,
  setIsTableChange,
  columnResizeMode,
}) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });

  useEffect(() => {
    if (isDragging) {
      setIsTableChange(isTableChange + 1);
    }
  }, [isDragging]);

  const user = useSelector((state) => state.auth);

  const style = {
    opacity: header.id === "action" ? 1 : 0.95,
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    whiteSpace: "nowrap",
    width: header.column.getSize(),
    zIndex: header.id === "action" ? 1 : 0,
    right:
      header.id === "action"
        ? `${header.column.getAfter("right")}px`
        : undefined,
    position: header.id === "action" ? "sticky" : "relative",
  };
  return (
    <th
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={style}
      className={`px-2 py-3 whitespace-nowrap text-left font-semibold first:pl-4 first:rounded-l-md last:rounded-r-md max-w-1/4 bg-[--bg-color-100]`}
    >
      <div className="inline-flex items-center space-x-2">
        {header.id !== "action" &&
          header.id !== "plus" &&
          user.type === Super_Admin && (
            <div
              {...attributes}
              {...listeners}
              className="fa-solid fa-bars text-xs text-slate-400"
            ></div>
          )}

        <div>
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>
      </div>
      {user.type === Super_Admin && (
        <div
          {...{
            onDoubleClick: () => header.column.resetSize(),
            onMouseDown: header.getResizeHandler(),
            onTouchStart: header.getResizeHandler(),
            className: `resizer ${table.options.columnResizeDirection} ${
              header.column.getIsResizing() ? "isResizing" : ""
            }`,
            style: {
              transform:
                columnResizeMode === "onEnd" && header.column.getIsResizing()
                  ? `translateX(${
                      (table.options.columnResizeDirection === "rtl" ? -1 : 1) *
                      (table.getState().columnSizingInfo.deltaOffset ?? 0)
                    }px)`
                  : "",
            },
          }}
        />
      )}
    </th>
  );
};

// Draggable Table Cell Component
const DragAlongCell = ({ cell, originalData, cellColumnName }) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const style = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    width: cell.column.getSize(),
    zIndex: isDragging ? 1 : 0,
    right:
      cell.column.id === "action"
        ? `${cell.column.getAfter("right")}px`
        : undefined,
    position: cell.column.id === "action" ? "sticky" : "relative",
  };

  return (
    <td
      style={style}
      ref={setNodeRef}
      title={
        cellColumnName !== "status" &&
        cellColumnName !== "ToDoAssignment" &&
        cellColumnName !== "action"
          ? originalData[cellColumnName]
          : null
      }
      className={`px-2 py-2 text-sm leading-4 tracking-tight first:pl-4 text-[#1E293B] text-left ${originalData?.super_row ? 'font-semibold bg-[--bg-color-50]' : 'bg-[--bg-color-50]'}  first:rounded-l-md last:rounded-r-md truncate`}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
};

export const DraggableTable = ({
  data,
  columns,
  columnOrder,
  setColumnOrder,
  loader,
  noRecords,
  pageName,
  isTableChange,
  setIsTableChange,
  ApiCallingFunction,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [columnResizeMode, setColumnResizeMode] = useState("onChange");
  const [columnResizeDirection, setColumnResizeDirection] = useState("ltr");

  const table = useReactTable({
    data,
    columns,
    columnResizeMode,
    columnResizeDirection,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnOrder,
    },
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
    defaultColumn: {
      maxSize: 800,
    },
  });

  // reorder columns after drag & drop
  function handleDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      if (over.id !== "action") {
        setColumnOrder((columnOrder) => {
          const oldIndex = columnOrder.indexOf(active.id);
          const newIndex = columnOrder.indexOf(over.id);
          return arrayMove(columnOrder, oldIndex, newIndex);
        });
      }
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  // Use the debounce hook to get the debounced version of the columns changes in customizable table
  const debounceHandleSizeChange = useDebounce(
    table.getState().columnSizing,
    5000
  );

  useEffect(() => {
    if (Object.keys(debounceHandleSizeChange).length > 0) {
      ApiCallingFunction(debounceHandleSizeChange);
    }
  }, [debounceHandleSizeChange]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className="overflow-x-auto min-h-[400px]">
        <table
          {...{
            style: {
              minWidth: "100%",
              width: table.getTotalSize(),
            },
          }}
          className="table-fixed w-full border-separate border-spacing-y-2"
        >
          <thead className="text-sm leading-2 tracking-tight font-semibold text-[#1E293B] bg-[--bg-color-100]]">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                <SortableContext
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <DraggableTableHeader
                      table={table}
                      key={header.id}
                      header={header}
                      isTableChange={isTableChange}
                      setIsTableChange={setIsTableChange}
                      columnResizeMode={columnResizeMode}
                    />
                  ))}
                </SortableContext>
              </tr>
            ))}
          </thead>

          <tbody className="text-sm">
            {loader ? (
              <tr className="max-h-[200px]">
                <td colSpan={columns?.length}>
                  <div className="flex justify-center whitespace-nowrap w-full h-full">
                    <Loader show={loader} />
                  </div>
                </td>
              </tr>
            ) : noRecords ? (
              <tr className="max-h-[200px]">
                <td colSpan={columns?.length}>
                  <div className="text-center py-2 text-lg text-[#1E293B] tracking-tight leading-4">
                    There are no records to display
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                return (
                  <tr
                    key={row.id}
                    className= {row?.original?.super_row ? "pointer-events-none" : "cursor-pointer"}
                    onClick={() => {
                      if (pageName) {
                        dispatch({ type: RESET_TODO_PERFORMANCE });
                        navigate(
                          `/todo/performance/${row.original.activity_assignment_id}?activity_id=${row.original.activity_id}`
                        );
                      } else {
                        dispatch({ type: RESET_TODO_PERFORMANCE });
                        navigate(
                          `/todo/performance/${row.original.assignment_id}`
                        );
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <SortableContext
                        key={cell.id}
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        <DragAlongCell
                          key={cell.id}
                          cell={cell}
                          originalData={row.original}
                          cellColumnName={cell.column.id}
                        />
                      </SortableContext>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </DndContext>
  );
};

Table.defaultProps = {
  tClass: "table-auto overflow-scroll w-full",
  hClass:
    "text-xs font-semibold uppercase text-slate-500 border-t border-b border-slate-200 ",
  bClass: "text-sm divide-y divide-slate-200 border-b border-slate-200 ",
  thClass: "px-2 py-5 whitespace-nowrap text-left ",
  tdClass: "px-2 py-3 whitespace-nowrap text-indigo-00",
};
export default Table;

// const TableCell = ({ children, text, arrow, tdClass, svgClass }) => {
//   const [open, setOpen] = useState(false);
//   return (
//     <td className={"px-2 first:pl-5 last:pr-5 whitespace-nowrap " + tdClass}>
//       {text || children}
//       {arrow && (
//         <svg
//           viewBox="0 0 500 400"
//           onClick={() => setOpen(!open)}
//           className={`w-3 h-3  ml-2 fill-current text-slate-400 ${open && "rotate-180"} ` + svgClass}
//         >
//           <path d="m249.4,387.4l-237.9,-227.76l61.68,-59.04l176.22,168.71l176.22,-168.71l61.68,59.04l-237.9,227.76z" />
//         </svg>
//       )}
//     </td>
//   );
// };

// TableCell.defaultProps = { tdClass: "", svgClass: "" };
